import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Visibility as ViewIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { RuleType, RuleVersion } from '../../types/leadSourceRules';
import { fetchData } from '../../helpers/apiUtils';
import moment from 'moment';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  ruleType: RuleType;
  onRevert: (versionId: number) => void;
}

export const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  ruleType,
  onRevert,
}) => {
  const [versions, setVersions] = useState<RuleVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState<RuleVersion | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [confirmRevertId, setConfirmRevertId] = useState<number | null>(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData(
        `/api/lead-source-rules/versions/${ruleType}?page=${page + 1}&pageSize=${pageSize}`,
      );
      if (response.success && response.data) {
        const data = response.data as { versions: RuleVersion[]; pagination: { total: number } };
        setVersions(data.versions);
        setTotal(data.pagination.total);
      } else {
        setError(response.error || 'Failed to load versions');
      }
    } catch (err) {
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [ruleType, page, pageSize]);

  useEffect(() => {
    if (open) {
      loadVersions();
    }
  }, [open, loadVersions]);

  const loadVersionDetails = async (id: number) => {
    try {
      const response = await fetchData(`/api/lead-source-rules/version/${id}`);
      if (response.success && response.data) {
        setSelectedVersion(response.data as RuleVersion);
        setViewDialogOpen(true);
      }
    } catch (err) {
      console.error('Failed to load version details:', err);
    }
  };

  const handleRevert = (id: number) => {
    setConfirmRevertId(id);
  };

  const confirmRevert = () => {
    if (confirmRevertId !== null) {
      onRevert(confirmRevertId);
      setConfirmRevertId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Version History - {ruleType.toUpperCase()} Rules</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : versions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No version history available
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((version) => (
                      <TableRow
                        key={version.id}
                        sx={{
                          backgroundColor: version.is_active ? '#e8f5e9' : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight="bold">v{version.version_number}</Typography>
                        </TableCell>
                        <TableCell>
                          {moment(version.created_at).format('DD.MM.YYYY HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {version.created_by}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {version.comment || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {version.is_active && (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                              icon={<ActiveIcon />}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => loadVersionDetails(version.id)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!version.is_active && (
                            <Tooltip title="Revert to this version">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRevert(version.id)}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Version Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Version {selectedVersion?.version_number} Details</DialogTitle>
        <DialogContent>
          {selectedVersion && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {moment(selectedVersion.created_at).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  By: {selectedVersion.created_by}
                </Typography>
                {selectedVersion.comment && (
                  <Typography variant="body2" color="text.secondary">
                    Comment: {selectedVersion.comment}
                  </Typography>
                )}
              </Box>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedVersion.rules, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Revert Dialog */}
      <Dialog open={confirmRevertId !== null} onClose={() => setConfirmRevertId(null)}>
        <DialogTitle>Confirm Revert</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revert to this version? This will create a new version with the
            selected rules and make it active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevertId(null)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmRevert}>
            Revert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
